import type { ShowDetailRecord } from '../../_lib/detail/show-detail-types';
import { formatDateRange, formatRuntime } from '../../_lib/detail/formatters';

interface ShowDetailsCardProps {
  show: ShowDetailRecord;
}

const TITLE_LIMIT = 60;

/**
 * Show Details Section — `brd-frontend.md` §4, `brd-backend.md` §2/§3/§4,
 * `frd.md` FR-DET-01..09. Each of the 4 grid items (age/date/venue/runtime)
 * hides independently when its own data is absent; category has no
 * documented hide rule (brd-backend.md §2) so it always renders here.
 * Renders bare (no `.shows-container` wrapper) — `page.tsx` places this
 * inside the two-column `.shows-detail-main-grid` left column.
 */
export function ShowDetailsCard({ show }: ShowDetailsCardProps) {
  const { event, venue } = show;

  const rawTitle = event.name || '';
  const displayTitle = rawTitle.length > TITLE_LIMIT ? `${rawTitle.slice(0, TITLE_LIMIT)}…` : rawTitle || 'Show Name is Unavailable';

  const dateRange = formatDateRange(event.startDate, event.endDate);
  const runtime = formatRuntime(event.runningTime);

  return (
    <section className="shows-detail-card shows-detail-details-card">
      <h2 className="shows-detail-details-title">{displayTitle}</h2>

      {/* Short marketing tagline (distinct from the full synopsis below) — omitted entirely, not
          an empty paragraph, when the show has none. */}
      {event.tagLine && <p className="shows-detail-details-tagline">{event.tagLine}</p>}

      <div className="shows-detail-details-grid">
        {event.minimumAge && (
          <div className="shows-detail-details-item">
            <i className="fa-light fa-family-pants" aria-hidden />
            <p>{event.minimumAge}</p>
          </div>
        )}

        {dateRange && (
          <div className="shows-detail-details-item">
            <i className="fa-light fa-calendar"></i>
            <div>
              <h3>Performance dates</h3>
              <p>{dateRange}</p>
            </div>
          </div>
        )}

        {venue.theatreName && (
          <div className="shows-detail-details-item">
            <i className="fa-light fa-location-dot" aria-hidden />
            <div>
              <h3>{venue.theatreName}</h3>
            </div>
          </div>
        )}

        {runtime && (
          <div className="shows-detail-details-item">
            <i className="fa-light fa-clock" aria-hidden />
            <div>
              <h3>Run time: {runtime.runtime}</h3>
              {runtime.interval && <p>{runtime.interval}</p>}
            </div>
          </div>
        )}

        {event.category.CategoryName && (
          <div className="shows-detail-details-item">
            <i className="fa-light fa-layer-group" aria-hidden />
            <div>
              <h3>Category</h3>
              <p>{event.category.CategoryName}</p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
